import { MESH_PROTOCOL_VERSION } from './types.js';
import { newCallId } from './tools.js';
/** "call"-shaped kinds — the asking side initiates these. */
const CALL_KINDS = new Set(['stage.load', 'stage.ping', 'stage.stop', 'stage.session.open']);
/** "result"-shaped kinds — a response or unsolicited push from a host. */
const RESULT_KINDS = new Set([
    'stage.ready',
    'stage.pong',
    'stage.progress',
    'stage.load.progress',
    'stage.token',
    'stage.session.accept',
    'stage.session.busy',
]);
// ── Id helpers (reuse tools.ts's mint, don't reinvent) ──────────────────
export { newCallId };
/**
 * Deterministic per-decode-step callId — the driver mints this before
 * sending the `sf` frame for step `seq`, and the responding host's
 * `stage.token` result echoes the same value (no `tc` call ever precedes
 * `stage.token`; this is the correlation token instead).
 *
 * Scoped by `sessionId`, not just `seq`: a continue-from-history replan
 * (`stageOrchestrator.ts`) restarts `seq` at 0 against a NEW session, so
 * without the session scope a late/stray `stage.token` from a since-
 * abandoned (possibly dead, possibly just slow) old session could
 * collide with the new session's seq=0 waiter and get misattributed —
 * exactly the kind of chaos CHAOS.md's seq/idempotency design is meant
 * to rule out.
 */
export function stageTokenCallId(sessionId, seq) {
    return `stagetok-${sessionId}-${seq}`;
}
export function newSessionId() {
    return `stagesess-${Math.random().toString(36).slice(2, 10)}${Math.random().toString(36).slice(2, 6)}`;
}
// ── Guards ───────────────────────────────────────────────────────────────
function isRecord(x) {
    return typeof x === 'object' && x !== null && !Array.isArray(x);
}
function isStringArr(x) {
    return Array.isArray(x) && x.every((s) => typeof s === 'string');
}
export function isStageLoadPayload(x) {
    if (!isRecord(x))
        return false;
    if (typeof x.sessionId !== 'string' || !x.sessionId)
        return false;
    if (typeof x.modelId !== 'string' || !x.modelId)
        return false;
    if (!Number.isInteger(x.layerStart) || x.layerStart < 0)
        return false;
    if (!Number.isInteger(x.layerEnd) || x.layerEnd <= x.layerStart)
        return false;
    if (!Number.isInteger(x.totalLayers) || x.totalLayers < x.layerEnd)
        return false;
    if (typeof x.includeEmbeddings !== 'boolean')
        return false;
    if (typeof x.includeOutput !== 'boolean')
        return false;
    if (x.manifestUrl === undefined && x.shardUrls === undefined)
        return false;
    if (x.manifestUrl !== undefined && typeof x.manifestUrl !== 'string')
        return false;
    if (x.shardUrls !== undefined && !isStringArr(x.shardUrls))
        return false;
    if (x.wireDtype !== 'f32' && x.wireDtype !== 'f16' && x.wireDtype !== 'i8')
        return false;
    if (!Number.isInteger(x.ctxSize) || x.ctxSize <= 0)
        return false;
    return true;
}
export function isStageReadyPayload(x) {
    if (!isRecord(x))
        return false;
    return (typeof x.sessionId === 'string' &&
        typeof x.isFirst === 'boolean' &&
        typeof x.isFinal === 'boolean' &&
        Number.isInteger(x.nEmbd) &&
        x.nEmbd > 0);
}
export function isStageStopPayload(x) {
    if (!isRecord(x))
        return false;
    return typeof x.sessionId === 'string' && typeof x.reason === 'string';
}
export function isStagePingPayload(x) {
    if (!isRecord(x))
        return false;
    return typeof x.sessionId === 'string' && typeof x.sentAtMs === 'number';
}
export function isStagePongPayload(x) {
    if (!isRecord(x))
        return false;
    return (typeof x.sessionId === 'string' &&
        typeof x.sentAtMs === 'number' &&
        typeof x.pongAtMs === 'number');
}
export function isStageProgressPayload(x) {
    if (!isRecord(x))
        return false;
    return (typeof x.sessionId === 'string' &&
        Number.isInteger(x.tokensDecoded) &&
        Number.isInteger(x.seq));
}
export function isStageLoadProgressPayload(x) {
    if (!isRecord(x))
        return false;
    if (typeof x.sessionId !== 'string' || !x.sessionId)
        return false;
    if (!Number.isInteger(x.shardsFetched) || x.shardsFetched < 0)
        return false;
    if (!Number.isInteger(x.totalShards) || x.totalShards < 0)
        return false;
    if (typeof x.bytesFetched !== 'number' || x.bytesFetched < 0)
        return false;
    if (x.totalBytes !== undefined && (typeof x.totalBytes !== 'number' || x.totalBytes < 0))
        return false;
    if (x.phase !== undefined && x.phase !== 'downloading' && x.phase !== 'opening' && x.phase !== 'warming')
        return false;
    return true;
}
export function isStageTokenPayload(x) {
    if (!isRecord(x))
        return false;
    if (typeof x.sessionId !== 'string')
        return false;
    if (!Number.isInteger(x.token))
        return false;
    if (!Number.isInteger(x.seq))
        return false;
    if (typeof x.done !== 'boolean')
        return false;
    if (x.finishReason !== undefined && typeof x.finishReason !== 'string')
        return false;
    if (x.text !== undefined && typeof x.text !== 'string')
        return false;
    return true;
}
export function isStageSessionOpenPayload(x) {
    if (!isRecord(x))
        return false;
    if (typeof x.sessionId !== 'string' || !x.sessionId)
        return false;
    if (typeof x.modelId !== 'string' || !x.modelId)
        return false;
    if (!Number.isInteger(x.layerStart) || x.layerStart < 0)
        return false;
    if (!Number.isInteger(x.layerEnd) || x.layerEnd <= x.layerStart)
        return false;
    if (!Number.isInteger(x.totalLayers) || x.totalLayers < x.layerEnd)
        return false;
    if (!Number.isInteger(x.ctxSize) || x.ctxSize <= 0)
        return false;
    if (x.wireDtype !== 'f32' && x.wireDtype !== 'f16' && x.wireDtype !== 'i8')
        return false;
    // wireHeader is now optional (a hop ≥2 omits it — see the field doc); when
    // present it must still be a non-empty base64 string.
    if (x.wireHeader !== undefined && (typeof x.wireHeader !== 'string' || !x.wireHeader))
        return false;
    // Relay fields (all optional, additive):
    if (x.stageIndex !== undefined && (!Number.isInteger(x.stageIndex) || x.stageIndex < 0))
        return false;
    if (x.isFinal !== undefined && typeof x.isFinal !== 'boolean')
        return false;
    if (x.nextPeerId !== undefined && (typeof x.nextPeerId !== 'string' || !x.nextPeerId))
        return false;
    if (x.prevPeerId !== undefined && (typeof x.prevPeerId !== 'string' || !x.prevPeerId))
        return false;
    // TEXT-RELAY fields (all optional, additive):
    if (x.promptText !== undefined && typeof x.promptText !== 'string')
        return false;
    if (x.textOutput !== undefined && typeof x.textOutput !== 'boolean')
        return false;
    return true;
}
export function isStageSessionAcceptPayload(x) {
    if (!isRecord(x))
        return false;
    if (typeof x.sessionId !== 'string' || !x.sessionId)
        return false;
    if (!Number.isInteger(x.nEmbd) || x.nEmbd <= 0)
        return false;
    if (typeof x.isFirst !== 'boolean')
        return false;
    if (typeof x.isFinal !== 'boolean')
        return false;
    if (!Number.isInteger(x.activeSessions) || x.activeSessions < 0)
        return false;
    if (!Number.isInteger(x.maxSessions) || x.maxSessions <= 0)
        return false;
    return true;
}
export function isStageSessionBusyPayload(x) {
    if (!isRecord(x))
        return false;
    if (typeof x.sessionId !== 'string' || !x.sessionId)
        return false;
    if (x.queuePosition !== undefined && (!Number.isInteger(x.queuePosition) || x.queuePosition < 0))
        return false;
    if (x.estWaitMs !== undefined && (typeof x.estWaitMs !== 'number' || x.estWaitMs < 0))
        return false;
    return true;
}
const PAYLOAD_GUARDS = {
    'stage.load': isStageLoadPayload,
    'stage.ready': isStageReadyPayload,
    'stage.stop': isStageStopPayload,
    'stage.ping': isStagePingPayload,
    'stage.pong': isStagePongPayload,
    'stage.progress': isStageProgressPayload,
    'stage.load.progress': isStageLoadProgressPayload,
    'stage.token': isStageTokenPayload,
    'stage.session.open': isStageSessionOpenPayload,
    'stage.session.accept': isStageSessionAcceptPayload,
    'stage.session.busy': isStageSessionBusyPayload,
};
// ── Encode ───────────────────────────────────────────────────────────────
/** Encode a `StageControlMessage` as a `MeshToolFrame` ready for `peer.sendTool`. */
export function encodeStageControl(msg) {
    const ts = Date.now();
    if (CALL_KINDS.has(msg.kind)) {
        const call = {
            v: MESH_PROTOCOL_VERSION,
            ts,
            callId: msg.callId,
            toolName: msg.kind,
            args: msg.payload,
        };
        return { kind: 'call', ...call };
    }
    const result = {
        v: MESH_PROTOCOL_VERSION,
        ts,
        callId: msg.callId,
        status: 'ok',
        result: { stageKind: msg.kind, payload: msg.payload },
    };
    return { kind: 'result', ...result };
}
// ── Decode ───────────────────────────────────────────────────────────────
/**
 * Decode a `MeshToolFrame` back into a `StageControlMessage`, or `null`
 * if it isn't one (not a stage frame at all, or a malformed one — v1
 * additive tolerance: never throw on garbage, just reject it).
 */
export function decodeStageControl(frame) {
    if (frame.kind === 'call') {
        const kind = frame.toolName;
        if (!CALL_KINDS.has(kind))
            return null;
        const guard = PAYLOAD_GUARDS[kind];
        if (!guard(frame.args))
            return null;
        const payload = frame.args;
        return { kind, callId: frame.callId, sessionId: frame.args.sessionId, payload };
    }
    // kind === 'result'
    if (frame.status !== 'ok' || !isRecord(frame.result))
        return null;
    const stageKind = frame.result.stageKind;
    if (typeof stageKind !== 'string' || !RESULT_KINDS.has(stageKind))
        return null;
    const kind = stageKind;
    const guard = PAYLOAD_GUARDS[kind];
    const payload = frame.result.payload;
    if (!guard(payload))
        return null;
    const typedPayload = payload;
    return {
        kind,
        callId: frame.callId,
        sessionId: payload.sessionId,
        payload: typedPayload,
    };
}
/** True iff a `MeshToolFrame` is (or claims to be) any stage-control message —
 * cheap pre-filter for a shared `onTool` handler that also carries ordinary
 * tool calls/results, before paying for the full decode + payload guard. */
export function isStageControlFrame(frame) {
    if (frame.kind === 'call')
        return CALL_KINDS.has(frame.toolName);
    return isRecord(frame.result) && typeof frame.result.stageKind === 'string' && RESULT_KINDS.has(frame.result.stageKind);
}
// ── Convenience constructors ─────────────────────────────────────────────
// Thin builders so callers (stageOrchestrator.ts, host-role code, tests)
// don't hand-assemble the discriminated union.
export function makeStageLoad(sessionId, payload, callId = newCallId()) {
    return { kind: 'stage.load', callId, sessionId, payload: { ...payload, sessionId } };
}
export function makeStageReady(sessionId, payload, callId) {
    return { kind: 'stage.ready', callId, sessionId, payload: { ...payload, sessionId } };
}
export function makeStageStop(sessionId, reason, callId = newCallId()) {
    return { kind: 'stage.stop', callId, sessionId, payload: { sessionId, reason } };
}
export function makeStagePing(sessionId, callId = newCallId()) {
    return { kind: 'stage.ping', callId, sessionId, payload: { sessionId, sentAtMs: Date.now() } };
}
export function makeStagePong(sessionId, sentAtMs, callId) {
    return { kind: 'stage.pong', callId, sessionId, payload: { sessionId, sentAtMs, pongAtMs: Date.now() } };
}
export function makeStageProgress(sessionId, tokensDecoded, seq, callId = newCallId()) {
    return { kind: 'stage.progress', callId, sessionId, payload: { sessionId, tokensDecoded, seq } };
}
/**
 * Load-progress push. `callId` defaults to a FRESH id on purpose (never
 * the load's callId) so this frame can't settle the load's ready/accept
 * waiter — see `StageLoadProgressPayload`'s doc.
 */
export function makeStageLoadProgress(sessionId, payload, callId = newCallId()) {
    return { kind: 'stage.load.progress', callId, sessionId, payload: { ...payload, sessionId } };
}
export function makeStageToken(sessionId, token, seq, done, finishReason, 
/** TEXT-RELAY — see `StageTokenPayload.text`'s doc comment. */
text) {
    return {
        kind: 'stage.token',
        callId: stageTokenCallId(sessionId, seq),
        sessionId,
        payload: {
            sessionId,
            token,
            seq,
            done,
            ...(finishReason !== undefined ? { finishReason } : {}),
            ...(text !== undefined ? { text } : {}),
        },
    };
}
export function makeStageSessionOpen(sessionId, payload, callId = newCallId()) {
    return { kind: 'stage.session.open', callId, sessionId, payload: { ...payload, sessionId } };
}
export function makeStageSessionAccept(sessionId, payload, callId) {
    return { kind: 'stage.session.accept', callId, sessionId, payload: { ...payload, sessionId } };
}
export function makeStageSessionBusy(sessionId, payload, callId) {
    return { kind: 'stage.session.busy', callId, sessionId, payload: { ...payload, sessionId } };
}
